import type { SalesOrder } from '@/lib/api/sales-order-api'

type OrderMoney = Pick<SalesOrder, 'total_amount' | 'paid_amount'> &
    Partial<Pick<SalesOrder, 'returned_amount' | 'return_fee_amount'>>

/**
 * Còn phải thu của đơn (M-44): tổng đơn − giá trị hàng khách đã trả + phí trả
 * hàng bên bán giữ lại − đã thu ròng. Âm = khách trả thừa. Cùng công thức
 * `DebtDue − DebtNetPaid` ở server.
 */
export function orderRemaining(o: OrderMoney): number {
    return (
        o.total_amount -
        (o.returned_amount ?? 0) +
        (o.return_fee_amount ?? 0) -
        (o.paid_amount ?? 0)
    )
}

/** Một dòng tính tiền: SL, đơn giá CHƯA VAT, chiết khấu dòng (%). */
export interface MoneyLine {
    quantity: number
    unitPrice: number
    discountPercent?: number
}

/** Giá một đơn vị đã gồm VAT, làm tròn về đồng (không VAT → giữ nguyên). */
export function vatInclusiveUnitPrice(
    unitPrice: number,
    vatPercent: number,
): number {
    return vatPercent > 0
        ? Math.round(unitPrice * (1 + vatPercent / 100))
        : unitPrice
}

/**
 * Tổng đơn gồm VAT (M-78): làm tròn giá gồm VAT của TỪNG đơn vị, nhân SL + CK
 * dòng + CK đơn, làm tròn từng dòng rồi cộng. Giá chưa VAT lẻ (100.000 / 1,08 =
 * 92.592,60) vẫn ra tổng tròn theo giá niêm yết. Cùng công thức
 * `entity.OrderGrossTotal` ở server — sửa cả hai.
 */
export function orderGrossTotal(
    lines: MoneyLine[],
    orderDiscountPercent: number,
    vatPercent: number,
): number {
    return lines.reduce(
        (sum, l) =>
            sum +
            Math.round(
                l.quantity *
                    vatInclusiveUnitPrice(l.unitPrice, vatPercent) *
                    (1 - (l.discountPercent ?? 0) / 100) *
                    (1 - orderDiscountPercent / 100),
            ),
        0,
    )
}

/**
 * Tiền VAT mặc định của đơn = tổng gồm VAT − tiền hàng đã làm tròn (`base`), để
 * tiền hàng + VAT luôn đúng bằng tổng. Không VAT → 0.
 */
export function vatFromGross(
    lines: MoneyLine[],
    orderDiscountPercent: number,
    vatPercent: number,
    base: number,
): number {
    if (vatPercent <= 0) return 0
    return Math.max(
        0,
        orderGrossTotal(lines, orderDiscountPercent, vatPercent) - base,
    )
}
